// Halaman awal per akun (G8 butir b, brief owner 15 Sep 2026) — SATU sumber aturan.
//
// Berkas ini SENGAJA tanpa import dan tanpa JSX: tes backend
// (`backend/tests/test_halaman_awal_per_role.py`) menjalankannya APA ADANYA di Node,
// bersama `can()`/`canWidget()` ASLI yang dipotong dari AuthContext.jsx. Aturan yang
// disalin ke berkas tes hanya menguji salinannya sendiri — yang diuji harus berkas ini.
//
// ATURANNYA: halaman awal = dashboard milik Template Hak Akses akun
// (`users.template_key`). Kalau template tidak ada, tidak dikenal, atau dashboardnya
// tidak bisa dibuka: role `finance_*` ke Finance, lalu dashboard pertama yang bisa
// dibuka dengan urutan tetap. Kalau tidak ada satu pun, "/" menampilkan panel tanpa
// dashboard. Karyawan murni tidak pernah sampai ke sini (pohon portal di App.js).
//
// KENAPA template, bukan menghitung centang izin: untuk role admin/viewer, modal
// "Atur Izin" diisi dari izin EFEKTIF (default admin = semua modul semua aksi), dan
// "Simpan Izin" menulis balik seluruh matriks itu. Satu kali simpan — misalnya hanya
// untuk mengatur cabang gudang — membuat hitungan centang = ukuran katalog modul, dan
// setiap admin tiba-tiba mendarat di modul yang katalognya paling panjang.
// `template_key` tidak disentuh jalur itu, dan ia memang kosakata owner untuk "jabatan".

/** Alamat alias Dashboard Sales & POS. "/" tetap merender Sales untuk akun yang tidak
 *  dialihkan, tapi MENU menunjuk ke sini: "/" yang dibuka di tab baru atau dari bookmark
 *  adalah KEDATANGAN dan dialihkan ke halaman awal — orang yang sengaja ingin melihat
 *  Sales tidak boleh ikut dilempar. */
export const ALAMAT_DASHBOARD_SALES = "/dashboard/sales";

export const HALAMAN_DASHBOARD = {
  sales: "/",
  hris: "/dashboard/hris",
  inventory: "/inventory/dashboard",
  finance: "/finance",
  marketing: "/marketing",
};

// Harus memuat SEMUA kunci `deps.PERMISSION_TEMPLATES` — dijaga tes. Preset baru tanpa
// baris di sini jatuh diam-diam ke urutan cadangan, jadi tesnya sengaja merah.
// `readonly_owner` ke Sales & POS: keputusan owner (a) — panel Ringkasan Keuangan dan
// Aktivitas Edit & Hapus tetap tinggal di Dashboard Sales & POS.
export const PRESET_KE_DASHBOARD = {
  staf_gudang: "inventory",
  kasir: "sales",
  manager_cabang: "sales",
  sales_lead: "sales",
  sales_field: "sales",
  front_liner: "sales",
  kasir_pos: "sales",
  usher: "sales",
  readonly_owner: "sales",
  hr_officer: "hris",
  finance_staff: "finance",
  finance_spv: "finance",
  finance_ceo: "finance",
  finance_auditor: "finance",
  marketing_manager: "marketing",
  marketing_admin: "marketing",
  customer_care: "marketing",
};

// `hris` terbuka hanya kalau `sales` terbuka (keduanya disuplai GET /dashboard), jadi
// posisinya setelah "sales" di sini tidak pernah teramati — HRIS hanya tercapai lewat
// template. Urutan tetap, bukan hitungan: lihat alasan di kepala berkas.
export const URUTAN_CADANGAN = ["sales", "hris", "inventory", "finance", "marketing"];

// = kunci `_boleh(user, "…")` di `routers/inventory/dashboard_inventory.py` ditambah dua
// kunci Request Stok (gerbang `boleh_lihat_request`). Dipakai MENU dan ATURAN halaman
// awal sekaligus — dua daftar yang berpisah jalan berarti orang dialihkan ke dashboard
// yang menunya tidak tampil, atau sebaliknya.
export const KUNCI_DASHBOARD_INVENTORY = [
  "inventory_laporan",
  "inventory_po",
  "inventory_transfer",
  "stock_request",
  "stock_request_approval",
  "inventory_opname",
];
// = `stock_request._KUNCI_TINJAU`: peninjau dengan `update` saja melihat bagian Request
// Stok walau tidak punya `view` di kunci mana pun.
export const KUNCI_PENINJAU_REQUEST = ["stock_request_approval", "stock_request"];

// Widget yang membuat Dashboard HRIS tidak kosong — dipakai HrisDashboard.jsx juga,
// supaya "HRIS bisa dibuka" di sini dan "ada yang tampil" di layar tidak berbeda arti.
export const WIDGET_HRIS_RINGKASAN = ["headcount", "payroll_total"];
export const WIDGET_HRIS_ABSENSI = "wow_attendance";

// Penanda state navigasi dari Login. Dibaca dengan `=== true` (bukan truthy) supaya
// state sembarang dari tempat lain tidak pernah terbaca sebagai "baru login".
export const TANDA_BARU_LOGIN = { baruLogin: true };

/** Role employee tanpa satu aksi pun yang menyala → pohon Portal Karyawan.
 *  Dipindah apa adanya dari App.js/Layout.jsx (dulu disalin di dua tempat). Sengaja
 *  TIDAK memeriksa `expires`: perilaku lama dipertahankan, dan dikunci tes. */
export function adalahKaryawanMurni(user) {
  const perms = user?.permissions;
  const ada = !!perms && Object.values(perms).some((m) => m && (m.view || m.create || m.update || m.delete));
  return user?.role === "employee" && !ada;
}

/** Gerbang rute /finance (FinanceGate di App.js memanggil fungsi INI). Aturan halaman
 *  awal hanya memilih Finance kalau gerbang yang sama meloloskan — kalau gerbang dan
 *  aturan berbeda, "/" dan /finance saling memantul tanpa henti. */
export function bolehMasukFinance(user) {
  if (user?.role === "admin" || user?.role === "superadmin") return true;
  if (user?.role && user.role.startsWith("finance_")) return true;
  const perms = user?.permissions || {};
  if (perms.finance?.view) return true;
  return Object.keys(perms).some((k) => k.startsWith("finance_") && perms[k]?.view);
}

/** Gerbang rute /marketing (MarketingGate di App.js), TANPA pengecualian halaman Task
 *  — pengecualian itu tetap di MarketingGate karena bukan pintu ke dashboard.
 *  `marketing_margin` dikecualikan: kunci itu bukan halaman, murni penentu tampil-
 *  tidaknya angka margin/ROI di halaman lain (PETA-MODUL.md 4.5). Pemegang kunci itu
 *  saja tidak berhak masuk ke satu pun halaman Marketing. */
export function bolehMasukMarketing(user) {
  if (user?.role === "admin" || user?.role === "superadmin") return true;
  const perms = user?.permissions || {};
  if (perms.marketing?.view) return true;
  return Object.keys(perms).some((k) => k.startsWith("marketing_") && k !== "marketing_margin" && perms[k]?.view);
}

/** Dashboard Inventory bisa dibuka = backend meloloskan minimal satu bagiannya.
 *  Himpunan ini ⊆ gerbang `dashboard_inventory.py` (view per bagian, atau peninjau
 *  Request Stok lewat `update`), jadi tujuan ini tidak pernah berakhir 403. */
export function bolehBukaDashboardInventory(can) {
  return KUNCI_DASHBOARD_INVENTORY.some((k) => can(k, "view"))
    || KUNCI_PENINJAU_REQUEST.some((k) => can(k, "update"));
}

/** Dashboard mana yang benar-benar bisa dibuka akun ini. Selalu lewat `can()` (bukan
 *  izin mentah) supaya izin berjangka yang sudah kedaluwarsa ikut menutup tujuannya. */
export function dashboardTerbuka(user, can, canWidget) {
  const sales = can("dashboard", "view"); // = gerbang GET /dashboard (dashboard.py)
  return {
    sales,
    // Dashboard HRIS tanpa satu widget pun cuma kotak "belum ada widget" — bukan tujuan.
    hris: sales && (WIDGET_HRIS_RINGKASAN.some((w) => canWidget(w)) || canWidget(WIDGET_HRIS_ABSENSI)),
    inventory: bolehBukaDashboardInventory(can),
    finance: bolehMasukFinance(user) && can("finance_dashboard", "view"),
    marketing: bolehMasukMarketing(user) && can("marketing_dashboard", "view"),
  };
}

/** Urutan dashboard yang dicoba: template, lalu role finance_*, lalu cadangan tetap.
 *  `hasOwnProperty` supaya kunci seperti "toString" tidak terbaca sebagai preset. */
export function kandidatDashboard(user) {
  const k = [];
  const tk = user?.template_key;
  if (typeof tk === "string" && Object.prototype.hasOwnProperty.call(PRESET_KE_DASHBOARD, tk)) {
    k.push(PRESET_KE_DASHBOARD[tk]);
  }
  if (typeof user?.role === "string" && user.role.startsWith("finance_")) k.push("finance");
  return [...k, ...URUTAN_CADANGAN];
}

export function halamanAwal(user, can, canWidget) {
  if (!user) return HALAMAN_DASHBOARD.sales;
  const buka = dashboardTerbuka(user, can, canWidget);
  const id = kandidatDashboard(user).find((x) => buka[x]);
  return id ? HALAMAN_DASHBOARD[id] : HALAMAN_DASHBOARD.sales;
}

/** Kedatangan = buka aplikasi/tab baru (router memberi key "default" untuk entri
 *  pertama) atau baru selesai login. Klik "Kembali ke HRIS" dan pantulan gerbang
 *  bukan kedatangan. */
export function adalahKedatanganBaru(location) {
  return location?.key === "default" || location?.state?.baruLogin === true;
}

/** Apa yang terjadi di "/". Dialihkan hanya saat KEDATANGAN, atau kalau Sales memang
 *  tidak bisa dibuka (merender Sales untuk mereka = kotak galat 403). Di luar itu
 *  orang yang boleh melihat Sales tetap melihatnya — "Kembali ke HRIS" harus sampai. */
export function keputusanBeranda(user, can, canWidget, baruMasuk) {
  const tujuan = halamanAwal(user, can, canWidget);
  const bisaSales = can("dashboard", "view");
  if (tujuan !== HALAMAN_DASHBOARD.sales && (baruMasuk || !bisaSales)) return { alihkan: tujuan };
  return { tampil: bisaSales ? "sales" : "tanpa-dashboard" };
}

/** Keputusan saat HalamanDepan pertama kali dipasang. `tandaiDibaca` = penanda
 *  kedatangan harus dikonsumsi (hanya kalau tidak dialihkan — kalau dialihkan, entri
 *  "/" sudah diganti Navigate). */
export function keputusanAwalHalamanDepan(location, user, can, canWidget) {
  const baruMasuk = adalahKedatanganBaru(location);
  const k = keputusanBeranda(user, can, canWidget, baruMasuk);
  return { ...k, tandaiDibaca: baruMasuk && !k.alihkan };
}

/** Keputusan pada render berikutnya. Kedatangan TIDAK dihitung ulang: polling
 *  /auth/me (izin berjangka) mengganti objek user saat orang duduk di "/", dan
 *  menghitung ulang dengan "baru datang" melemparnya dari Sales di tengah kerja.
 *  Yang tetap dihitung ulang hanya "Sales masih bisa dibuka?". */
export function keputusanHalamanDepan(awal, user, can, canWidget) {
  if (awal.alihkan) return { alihkan: awal.alihkan };
  return keputusanBeranda(user, can, canWidget, false);
}

/** Ke mana "/" akan memantul untuk orang yang TIDAK sedang datang — dipakai layout
 *  Finance/Marketing untuk menyembunyikan "Kembali ke HRIS" yang ujungnya kembali ke
 *  modul yang sama (tombol mati). `null` = "/" menampilkan sesuatu, tombol berguna. */
export function berandaMemantulKe(user, can, canWidget) {
  return keputusanBeranda(user, can, canWidget, false).alihkan || null;
}
